import { createSlice } from "@reduxjs/toolkit";

import i18next from "i18next";

import { RootState } from "@/redux";
import { AuthState } from "@/types/auth";

import { accountApi } from "@/pages/Private/redux/account/account.api";

import { coursesProgressApi } from "@/pages/Private/redux/course-progress/course-progress.api";

import { PermissionRoles } from "@/enum";

import { authApi } from "./auth.api";

const token = localStorage.getItem("token");
const otpToken = sessionStorage.getItem("otpToken");
const pendingEmailOtpUserId = sessionStorage.getItem("pendingEmailOtpUserId");

const hasActiveSession = token || otpToken || pendingEmailOtpUserId;

if (!hasActiveSession) {
	sessionStorage.removeItem("otpToken");
	sessionStorage.removeItem("pendingEmailOtpUserId");
	sessionStorage.removeItem("pendingTwoFaUserId");
}

const initialState: AuthState = {
	otpToken: token ? sessionStorage.getItem("otpToken") || null : null,
	token: token || null,
	pendingToken: null,
	pendingUser: null,
	user: null,
	pendingEmailOtpUserId: token
		? sessionStorage.getItem("pendingEmailOtpUserId")
			? Number(sessionStorage.getItem("pendingEmailOtpUserId"))
			: null
		: null,
};

export const authSlice = createSlice({
	name: "auth",
	initialState,
	reducers: {
		resetAuth: (state) => {
			localStorage.clear();
			sessionStorage.removeItem("otpToken");
			sessionStorage.removeItem("pendingEmailOtpUserId");
			sessionStorage.removeItem("pendingTwoFaUserId");
			state.user = null;
			state.token = null;
			state.otpToken = null;
			state.pendingEmailOtpUserId = null;
			state.pendingToken = null;
			state.pendingUser = null;
		},
		setAuth: (state, { payload }) => {
			const { otpToken, accessToken, token, user } = payload;

			if (accessToken) {
				localStorage.setItem("token", accessToken);
				state.token = accessToken;
			} else if (token) {
				localStorage.setItem("token", token);
				state.token = token;
			}

			if (otpToken) {
				state.otpToken = otpToken;
			}

			state.user = user;
		},
		backTo2FA: (state) => {
			state.pendingEmailOtpUserId = null;
			sessionStorage.removeItem("pendingEmailOtpUserId");
			const otpToken = sessionStorage.getItem("otpToken");

			if (otpToken) {
				state.otpToken = otpToken;
			}
		},
		confirmAuth: (state) => {
			if (state.pendingToken) {
				localStorage.setItem("token", state.pendingToken);
				state.token = state.pendingToken;
				state.pendingToken = null;
			}

			if (state.pendingUser) {
				state.user = state.pendingUser;
				state.pendingUser = null;
			}
		},
		updatePermissions: (state, { payload }) => {
			if (state?.user?.role?.permissions) {
				state.user.role.permissions = (payload?.permissions ?? []).map((code: PermissionRoles) => ({
					code,
				}));
			}
		},
		setPendingEmailOtpUserId: (state, action) => {
			state.pendingEmailOtpUserId = action.payload;
			state.otpToken = null;
			sessionStorage.setItem("pendingEmailOtpUserId", String(action.payload));
		},
		clearPendingEmailOtpUserId: (state) => {
			state.pendingEmailOtpUserId = null;
			sessionStorage.removeItem("pendingEmailOtpUserId");
		},
		clearOtpToken: (state) => {
			state.otpToken = null;
			sessionStorage.removeItem("otpToken");
		},
	},
	extraReducers: (builder) => {
		builder
			.addMatcher(authApi.endpoints.login.matchFulfilled, (state, { payload }) => {
				const { otpToken, accessToken, user, isRememberMe } = payload;

				if (otpToken) {
					state.otpToken = otpToken;
					sessionStorage.setItem("otpToken", otpToken);
					sessionStorage.setItem("pendingTwoFaUserId", String(user.id));
					state.user = user;

					return;
				}

				if (user?.isEmailOtpEnabled) {
					state.pendingEmailOtpUserId = user.id;
					sessionStorage.setItem("pendingEmailOtpUserId", String(user.id));
					state.user = user;
					if (accessToken) {
						if (isRememberMe) {
							localStorage.setItem("token", accessToken);
						}

						state.token = accessToken;
					}

					state.user = user;

					return;
				}

				if (accessToken) {
					if (isRememberMe) {
						localStorage.setItem("token", accessToken);
					}

					state.token = accessToken;
				}

				state.user = user;
			})
			.addMatcher(authApi.endpoints.verifyEmailOtp.matchFulfilled, (state, { payload }) => {
				sessionStorage.removeItem("pendingEmailOtpUserId");
				sessionStorage.removeItem("otpToken");

				const { accessToken, user } = payload;

				if (accessToken) {
					localStorage.setItem("token", accessToken);
					state.token = accessToken;
					state.pendingEmailOtpUserId = null;
					state.pendingToken = null;
				}

				state.user = user;
			})
			.addMatcher(authApi.endpoints.acceptInvitation.matchFulfilled, (state, { payload }) => {
				const { accessToken, user } = payload;

				if (accessToken) {
					state.pendingToken = accessToken;
				}

				if (user) {
					state.pendingUser = user;
				}
			})
			.addMatcher(authApi.endpoints.fetchMe.matchFulfilled, (state, { payload }) => {
				state.user = payload;
				i18next.changeLanguage(payload.userLanguage?.toLowerCase());
			})
			.addMatcher(accountApi.endpoints.updateMe.matchFulfilled, (state, { payload }) => {
				state.user = payload;
			})
			.addMatcher(authApi.endpoints.authTwoFactorCode.matchFulfilled, (state, { payload }) => {
				sessionStorage.removeItem("pendingTwoFaUserId");
				sessionStorage.removeItem("otpToken");
				sessionStorage.removeItem("pendingEmailOtpUserId");
				const { accessToken, user } = payload;

				if (accessToken) {
					localStorage.setItem("token", accessToken);
					state.token = accessToken;
					state.otpToken = null;
					state.pendingEmailOtpUserId = null;
				}

				state.user = user;
			})
			.addMatcher(accountApi.endpoints.verifyTwoFactor.matchFulfilled, (state, { payload }) => {
				const { accessToken, user } = payload;

				if (accessToken) {
					localStorage.setItem("token", accessToken);
					state.token = accessToken;
					state.pendingToken = null;
					state.otpToken = null;
				}

				if (user) {
					state.user = user;
					state.pendingUser = null;
				}
			})
			.addMatcher(accountApi.endpoints.disableTwoFactor.matchFulfilled, (state, { payload }) => {
				state.user = payload;
			})
			.addMatcher(
				coursesProgressApi.endpoints.acceptCourseProgressInvitation.matchFulfilled,
				(state, { payload }) => {
					const { accessToken, user } = payload;

					if (accessToken) {
						localStorage.setItem("token", accessToken);
						state.token = accessToken;
						state.otpToken = null;
					}

					state.user = user;
				}
			)
			.addMatcher(
				coursesProgressApi.endpoints.checkIfAlreadyAccepted.matchFulfilled,
				(state, { payload }) => {
					const { accessToken, user } = payload;

					if (accessToken) {
						localStorage.setItem("token", accessToken);
						state.token = accessToken;
						state.otpToken = null;
					}

					state.user = user;
				}
			)
			.addMatcher(
				coursesProgressApi.endpoints.authorizeInScenarioProgressViaOtp.matchFulfilled,
				(state, { payload }) => {
					const { isFirstLogin } = payload;

					if (!isFirstLogin) {
						if (payload?.accessToken) {
							localStorage.setItem("token", payload?.accessToken);
							state.token = payload?.accessToken;
							state.otpToken = null;
						}

						if (payload?.user) {
							state.user = payload.user;
						}
					}
				}
			)
			.addMatcher(
				coursesProgressApi.endpoints.acceptCourseProgressInvitation.matchFulfilled,
				(state, { payload }) => {
					const { accessToken, user } = payload;

					if (accessToken) {
						localStorage.setItem("token", accessToken);
						state.token = accessToken;
						state.otpToken = null;
					}

					state.user = user;
				}
			);
	},
});

export const {
	resetAuth,
	setAuth,
	updatePermissions,
	confirmAuth,
	setPendingEmailOtpUserId,
	clearPendingEmailOtpUserId,
	clearOtpToken,
	backTo2FA,
} = authSlice.actions;
export const authReducer = authSlice.reducer;
export const authSelector = (state: RootState) => state[authSlice.name];
